
import React, { memo, useCallback, useEffect, useState, useRef } from 'react';


import MainCanvas from '../MainCanvas';
import { ROOT_CONTAINER_CLASS_NAME } from '../../utils/constants';
import Topbar from '../../components/Topbar';
import LayersPanelFlyout from '../../components/Topbar/LayersPanelFlyout';
import Tabs from '../../components/Tabs';
import ToolsBar from '../../components/ToolsBar';
import EditorTopbarHelp from '../../../../EditorTopbarHelp';
import {
  HIDE_LOADER,
  SET_FEEDBACK,
  SET_ORIGINAL_IMAGE,
  SHOW_LOADER,
  UPDATE_STATE,
  SELECT_TAB,
} from '../../actions';
import NotificationPopup from '../../components/NotificationPopup';
import loadImage from '../../utils/loadImage';
import {
  useEditorKeyboardShortcuts,
  usePhoneScreen,
  useResizeObserver,
  useStore,
  useTransformedImgData,
} from '../../hooks';
import Spinner from '../../components/common/Spinner';
// import { getBackendTranslations } from '../../utils/translator';
import cloudimageQueryToDesignState from '../../utils/cloudimageQueryToDesignState';
import finetunesStrsToClasses from '../../utils/finetunesStrsToClasses';
import filterStrToClass from '../../utils/filterStrToClass';
import isSameImage from '../../utils/isSameImage';
import normalizeLoadedAnnotationsStacking from '../../utils/normalizeLoadedAnnotationsStacking';
import preloadAnnotationFonts from '../../utils/preloadAnnotationFonts';
import scaleAnnotationsForDimensions from '../../utils/scaleAnnotationsForDimensions';
import normalizeAnnotationFontStylesForLoad from '../../utils/normalizeAnnotationFontStylesForLoad';
import { ensureGoogleTextFontsLoaded } from '../../../../textFonts.config';
import expandTextAnnotationsToFitContent, {
  collectClippedTextAnnotationPatches,
} from '../../utils/expandTextAnnotationsToFitContent';
import {
  StyledAppWrapper,
  StyledMainContent,
  StyledCanvasAndTools,
  StyledPhoneToolsAndTabs,
} from './App.styled';
import defaultTranslations from '../../context/defaultTranslations';

const App = () => {
  const {
    config,
    isLoadingGlobally,
    haveNotSavedChanges,
    dispatch,
    originalImage,
    shownImageDimensions,
    t,
    feedback = {},
    designLayer,
    tabId = null,
  } = useStore();

  useEditorKeyboardShortcuts();
  const {
    loadableDesignState,
    useCloudimage,
    cloudimage,
    source,
    avoidChangesNotSavedAlertOnLeave,
    useBackendTranslations,
    translations,
    language,
    defaultSavedImageName,
    observePluginContainerSize,
    showCanvasOnly,
    getCurrentImgDataFnRef,
    updateStateFnRef,
    selectTabFnRef,
  } = config;

  const [observeResize, unobserveElement] = useResizeObserver();
  const [rootSize, setRootSize] = useState({
    width: undefined,
    height: undefined,
  });
  const isPhoneScreen = usePhoneScreen();
  const pluginRootRef = useRef(null);
  const isFirstRender = useRef(true);
  const cloudimageQueryLoaded = useRef(false);
  const imageBeingLoadedSrc = useRef(null);
  const shownImageDimensionsRef = useRef(shownImageDimensions);
  const designLayerRef = useRef(null);
  // Hacky solution, For being used in beforeunload event
  // as it won't be possible to have the latest value of the state variable in js event handler.
  const haveNotSavedChangesRef = useRef(haveNotSavedChanges);
  const transformImgFn = useTransformedImgData();

  shownImageDimensionsRef.current = shownImageDimensions;
  designLayerRef.current = designLayer;

  const setNewOriginalImage = useCallback((newOriginalImage) => {
    dispatch({
      type: SET_ORIGINAL_IMAGE,
      payload: {
        originalImage: newOriginalImage,
      },
    });
  }, []);

  const setError = useCallback((newError) => {
    dispatch({
      type: SET_FEEDBACK,
      payload: {
        feedback: {
          message: newError.message || newError,
          duration: 0,
        },
      },
    });
  }, []);

  // We are promisifying the image loading for mixing it with other promises
  const loadAndSetOriginalImage = (imgToLoad) =>
    new Promise((resolve) => {
      const imgSrc = imgToLoad?.src || imgToLoad;
      if (
        imageBeingLoadedSrc.current === imgSrc ||
        (!imgSrc && originalImage) ||
        isSameImage(imgSrc, originalImage)
      ) {
        resolve();
        return;
      }

      const triggerResolve = () => {
        imageBeingLoadedSrc.current = null;
        resolve();
      };

      imageBeingLoadedSrc.current = imgSrc;

      if (typeof imgToLoad === 'string') {
        loadImage(imgToLoad, defaultSavedImageName)
          .then(setNewOriginalImage)
          .catch(setError)
          .finally(triggerResolve);
      } else if (imgToLoad instanceof HTMLImageElement) {
        setNewOriginalImage(imgToLoad);
        triggerResolve();
      } else {
        setError(t('invalidImageError'));
        triggerResolve();
      }
    });

  const promptDialogIfHasChangeNotSaved = (e) => {
    if (haveNotSavedChangesRef.current) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  // loadingPromisesFn is a function for enabling the ability to show loader first then trigger requests not vice versa.
  const handleLoading = (loadingPromisesFn = () => []) => {
    dispatch({ type: SHOW_LOADER });

    return Promise.all(loadingPromisesFn()).finally(() => {
      dispatch({ type: HIDE_LOADER });
    });
  };

  const updateDesignStateWithLoadableOne = useCallback(
    (currentDimensions = shownImageDimensionsRef.current) => {
      if (loadableDesignState && Object.keys(loadableDesignState).length > 0) {
        const {
          shownImageDimensions: savedDimensions,
          imgSrc: _imgSrc,
          ...restDesignState
        } = loadableDesignState;
        const normalizedFontAnnotations = normalizeAnnotationFontStylesForLoad(
          restDesignState.annotations,
        );
        const scaledAnnotations = scaleAnnotationsForDimensions(
          normalizedFontAnnotations,
          savedDimensions,
          currentDimensions,
        );
        const fittedAnnotations = expandTextAnnotationsToFitContent(scaledAnnotations);
        const normalizedAnnotations =
          normalizeLoadedAnnotationsStacking(fittedAnnotations);

        dispatch({
          type: UPDATE_STATE,
          payload: {
            ...restDesignState,
            ...(normalizedAnnotations ? { annotations: normalizedAnnotations } : {}),
            finetunes: finetunesStrsToClasses(loadableDesignState?.finetunes),
            filter: filterStrToClass(loadableDesignState?.filter),
          },
        });

        const applyClippedTextPatches = () => {
          const layer = designLayerRef.current;
          const patched = collectClippedTextAnnotationPatches(
            layer,
            normalizedAnnotations,
          );
          if (patched) {
            dispatch({
              type: UPDATE_STATE,
              payload: { annotations: patched },
            });
          }
          layer?.find?.('Text')?.forEach((node) => {
            node._setTextData?.();
            node.clearCache?.();
          });
          layer?.batchDraw?.();
        };

        requestAnimationFrame(() => {
          applyClippedTextPatches();
          // Webfonts in TextNode resolve async — one more pass after they settle.
          requestAnimationFrame(applyClippedTextPatches);
          if (typeof document !== 'undefined' && document.fonts?.ready) {
            document.fonts.ready.then(() => {
              requestAnimationFrame(applyClippedTextPatches);
            });
          }
        });
      }
    },
    [dispatch, loadableDesignState],
  );

  const waitForShownImageDimensions = useCallback(
    () =>
      new Promise((resolve) => {
        let attempts = 0;
        let stableFrames = 0;
        let previousDimensionsKey = '';
        const tick = () => {
          const dims = shownImageDimensionsRef.current;
          const dimensionsKey = dims
            ? `${dims.width}:${dims.height}:${dims.x}:${dims.y}:${dims.scaledBy}`
            : '';
          if (
            dims?.width > 0 &&
            dims?.height > 0 &&
            designLayerRef.current
          ) {
            stableFrames =
              dimensionsKey === previousDimensionsKey ? stableFrames + 1 : 0;
            if (stableFrames >= 2) {
              resolve({ ...dims });
              return;
            }
            previousDimensionsKey = dimensionsKey;
          } else {
            stableFrames = 0;
            previousDimensionsKey = dimensionsKey;
          }
          if (attempts >= 120) {
            resolve({ ...(shownImageDimensionsRef.current || {}) });
            return;
          }
          attempts += 1;
          requestAnimationFrame(tick);
        };
        tick();
      }),
    [],
  );

  const applyLoadableDesignState = useCallback(async () => {
    if (!loadableDesignState || Object.keys(loadableDesignState).length === 0) {
      return;
    }
    const currentDimensions = await waitForShownImageDimensions();
    ensureGoogleTextFontsLoaded();
    const annotationsForPreload = normalizeAnnotationFontStylesForLoad(
      loadableDesignState.annotations,
    );
    await preloadAnnotationFonts(annotationsForPreload);
    updateDesignStateWithLoadableOne(currentDimensions);
  }, [
    loadableDesignState,
    updateDesignStateWithLoadableOne,
    waitForShownImageDimensions,
  ]);

  useEffect(() => {
    if (
      !isFirstRender.current &&
      source &&
      !isSameImage(source, originalImage)
    ) {
      cloudimageQueryLoaded.current = false;
      handleLoading(() => [loadAndSetOriginalImage(source)]);
    }
  }, [source]);

  useEffect(() => {
    if (!isFirstRender.current) {
      const newImgSrc = loadableDesignState?.imgSrc;
      if (newImgSrc && !isSameImage(newImgSrc, originalImage)) {
        handleLoading(() => [
          loadAndSetOriginalImage(newImgSrc).then(() => applyLoadableDesignState()),
        ]);
      } else if (loadableDesignState && Object.keys(loadableDesignState).length > 0) {
        handleLoading(() => [applyLoadableDesignState()]);
      }
    }
  }, [loadableDesignState]);

  useEffect(() => {
    if (
      Object.keys(shownImageDimensions || {}).length > 0 &&
      !Object.keys(shownImageDimensions).some(
        (k) => !shownImageDimensions[k],
      ) &&
      originalImage &&
      useCloudimage &&
      cloudimage?.loadableQuery &&
      !cloudimageQueryLoaded.current
    ) {
      dispatch({
        type: UPDATE_STATE,
        payload: cloudimageQueryToDesignState(
          cloudimage.loadableQuery,
          shownImageDimensions,
          originalImage,
        ),
      });
      cloudimageQueryLoaded.current = true;
    }
  }, [shownImageDimensions, originalImage, useCloudimage, cloudimage]);

  useEffect(() => {
    let isUnmounted = false;
    if (observePluginContainerSize && pluginRootRef.current) {
      observeResize(pluginRootRef.current.parentNode, ({ width, height }) =>
        setRootSize({ width, height }),
      );
    } else if (rootSize.width && rootSize.height && !isUnmounted) {
      setRootSize({ width: undefined, height: undefined });
    }

    return () => {
      if (observePluginContainerSize && pluginRootRef.current) {
        unobserveElement(pluginRootRef.current);
      }

      isUnmounted = true;
    };
  }, [observePluginContainerSize]);

  useEffect(() => {
    const initialRequestsPromisesFn = () => [
      loadAndSetOriginalImage(loadableDesignState?.imgSrc || source).then(() =>
        applyLoadableDesignState(),
      ),
      ...(useBackendTranslations
        ? [defaultTranslations]
        : []),
    ];

    handleLoading(initialRequestsPromisesFn);
    isFirstRender.current = false;

    if (window && !avoidChangesNotSavedAlertOnLeave) {
      window.addEventListener('beforeunload', promptDialogIfHasChangeNotSaved);
    }

    return () => {
      if (window && !avoidChangesNotSavedAlertOnLeave) {
        window.removeEventListener(
          'beforeunload',
          promptDialogIfHasChangeNotSaved,
        );
      }
    };
  }, []);

  useEffect(() => {
    if (updateStateFnRef && typeof updateStateFnRef === 'object') {
      updateStateFnRef.current = (newStatePartObjOrFn) => {
        dispatch({
          type: UPDATE_STATE,
          payload: newStatePartObjOrFn,
        });
      };
    }
  }, [updateStateFnRef, dispatch]);

  useEffect(() => {
    if (selectTabFnRef && typeof selectTabFnRef === 'object') {
      selectTabFnRef.current = (tabId) => {
        dispatch({
          type: SELECT_TAB,
          payload: { tabId },
        });
      };
    }
  }, [selectTabFnRef, dispatch]);

  useEffect(() => {
    if (getCurrentImgDataFnRef && typeof getCurrentImgDataFnRef === 'object') {
      getCurrentImgDataFnRef.current = transformImgFn;
    }
  }, [transformImgFn]);

  useEffect(() => {
    haveNotSavedChangesRef.current = haveNotSavedChanges;
  }, [haveNotSavedChanges]);

  const retryLoadingImage = () => {
    dispatch({
      type: SET_FEEDBACK,
      payload: { feedback: {} },
    });
    handleLoading(() => [
      loadAndSetOriginalImage(loadableDesignState?.imgSrc || source).then(() =>
        applyLoadableDesignState(),
      ),
    ]);
  };

  const hasImageLoadFailed =
    !isLoadingGlobally && (!originalImage || feedback.duration === 0);

  return (
    <StyledAppWrapper
      className={ROOT_CONTAINER_CLASS_NAME}
      data-phone={isPhoneScreen}
      ref={pluginRootRef}
      $size={rootSize}
    >
      {isLoadingGlobally && <Spinner label={t('loading')} />}
      {hasImageLoadFailed && (
        <div
          className="respark_editor-error-state"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            gap: '12px',
            textAlign: 'center',
            padding: '24px',
            color: '#5b6577',
          }}
        >
          <p style={{ margin: 0, fontWeight: 600, color: '#1f2a44' }}>
            Couldn't load the image
          </p>
          <p style={{ margin: 0, maxWidth: 420, fontSize: 13 }}>
            {feedback.message ||
              'The selected template or background image failed to load. Please try again or pick another template.'}
          </p>
          <button
            type="button"
            onClick={retryLoadingImage}
            style={{
              cursor: 'pointer',
              border: 'none',
              borderRadius: 8,
              padding: '8px 18px',
              background: '#ef5d8f',
              color: '#fff',
              fontWeight: 600,
            }}
          >
            Retry
          </button>
        </div>
      )}
      {originalImage && feedback.duration !== 0 && (
        <StyledMainContent
          className={`respark_main-container${
            tabId == null ? ' respark-sidebar-tab-deselected' : ''
          }`}
        >
          <div className="left-nav">
            {!isPhoneScreen && !showCanvasOnly && <Tabs />}
            {!isPhoneScreen && !showCanvasOnly && (
              <EditorTopbarHelp className="respark_sidebar-help-buttons" />
            )}
            {!isPhoneScreen && !showCanvasOnly && config.logo && (
              <div className="respark_sidebar-logo">
                <img src={config.logo} alt={t('companyLogo') || 'Company logo'} />
              </div>
            )}
          </div>
          <div className="right-content">

            <div className="tool-bar-wrap">
              {!showCanvasOnly &&
                isPhoneScreen ?
                <StyledPhoneToolsAndTabs className="respark_phone-tools-tabs-wrapper">
                  <ToolsBar />
                  <Tabs />
                </StyledPhoneToolsAndTabs>
                : <ToolsBar />}
            </div>

            <div className="top-bar-canvas-wrap">
              {!showCanvasOnly && <Topbar />}
              {!showCanvasOnly && <LayersPanelFlyout />}
              <StyledCanvasAndTools className="respark_editor-content">
                <MainCanvas />
              </StyledCanvasAndTools>
            </div>

          </div>
        </StyledMainContent>
      )}
      <NotificationPopup />
    </StyledAppWrapper>
  );
};

export default memo(App);
